// /portal/compliance upload modal (portal-kyc Phase D).
//
// Flow (PLAN §6a + portal_ux upload_flow):
//   1. client-side mime + 10MB check (fast feedback; server is source of truth)
//   2. POST /api/portal/kyc/upload-url  → { upload_url, blob_name, expires_at }
//   3. browser PUT to the Azure SAS URL with x-ms-blob-type: BlockBlob + the
//      file's content-type (progress bar; abortable via XHR)
//   4. POST /api/portal/kyc/documents   → registers the metadata row
//
// Orphan-retry (R20): if the PUT succeeds but step 4 fails, we keep the
// blob_name in component state and offer a "retry recording" button that
// re-runs step 4 only — we do NOT re-upload the file.

import { useRef, useState } from 'react';
import { apiPost, ApiError } from '../../lib/api';
import type { DocumentDto, KycCategoryConfig } from '@shared';

// Operational allowlist — mirrors the server (ALLOWED_MIME in portal-kyc.ts).
// Not regulatory copy; safe to hardcode.
const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];
const ACCEPT_ATTR = ALLOWED_MIME.join(',');
const SIZE_CAP_BYTES = 10 * 1024 * 1024; // 10MB

interface UploadUrlResponse {
  upload_url: string;
  blob_name: string;
  expires_at: string;
}

type Phase = 'pick' | 'requesting' | 'uploading' | 'recording' | 'orphan' | 'done';

interface Props {
  category: KycCategoryConfig;
  /** "Upload" for a fresh slot, "Replace" when a doc already exists. Affects copy only. */
  mode: 'upload' | 'replace';
  onClose: () => void;
  onUploaded: (doc: DocumentDto) => void;
  /** Fired when a gated call returns 412 kyc_consent_required. The parent opens
   *  the consent modal; the upload modal stays mounted so the user can retry. */
  onConsentRequired: () => void;
}

// PUT the file straight to Azure Blob storage via the SAS URL. Returns a
// promise that resolves on 2xx and rejects otherwise. Reports progress and
// is abortable through the supplied AbortSignal.
function putToAzure(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    // Azure block-blob PUT requires this header; content-type pins the stored
    // mime so the server's post-PUT getProperties() cross-check passes.
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('content-type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Storage rejected the upload (status ${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error('Network error while uploading to storage.'));
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));

    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

export function ComplianceUpload({ category, mode, onClose, onUploaded, onConsentRequired }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>('pick');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Held for the orphan-retry path — the blob is already in storage, we just
  // need to re-run the metadata POST.
  const orphanRef = useRef<{ blob_name: string; filename: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const clientValidate = (f: File): string | null => {
    if (!ALLOWED_MIME.includes(f.type)) {
      return 'Only PDF, JPEG and PNG files are accepted.';
    }
    if (f.size > SIZE_CAP_BYTES) {
      return `File is too large. The limit is 10MB (this file is ${(f.size / 1024 / 1024).toFixed(1)}MB).`;
    }
    return null;
  };

  const onPick = (f: File | null) => {
    setError(null);
    if (!f) { setFile(null); return; }
    const v = clientValidate(f);
    if (v) { setError(v); setFile(null); return; }
    setFile(f);
  };

  // Step 4 in isolation — used both on the happy path and for orphan retry.
  async function recordMetadata(blob_name: string, f: { name: string; type: string; size: number }) {
    setPhase('recording');
    setError(null);
    try {
      const r = await apiPost<{ document: DocumentDto }>('/api/portal/kyc/documents', {
        blob_name,
        filename: f.name,
        mime_type: f.type,
        size_bytes: f.size,
        kyc_category: category.key,
        title: category.label,
      });
      orphanRef.current = null;
      setPhase('done');
      onUploaded(r.document);
    } catch (err) {
      // The file is in storage but the row failed to write — keep blob_name so
      // the user can retry the recording step without re-uploading (R20).
      orphanRef.current = { blob_name, filename: f.name };
      setPhase('orphan');
      setError(err instanceof ApiError ? err.serverMessage : 'Could not record the upload. You can retry.');
    }
  }

  async function start() {
    if (!file) return;
    setError(null);
    setProgress(0);
    setPhase('requesting');
    try {
      const sas = await apiPost<UploadUrlResponse>('/api/portal/kyc/upload-url', {
        filename: file.name,
        mime_type: file.type,
        size_bytes: file.size,
        kyc_category: category.key,
      });

      setPhase('uploading');
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      await putToAzure(sas.upload_url, file, setProgress, ctrl.signal);
      abortRef.current = null;

      await recordMetadata(sas.blob_name, file);
    } catch (err) {
      abortRef.current = null;
      if (err instanceof DOMException && err.name === 'AbortError') {
        setPhase('pick');
        setError(null);
        return;
      }
      // Consent gate — hand control to the parent to capture consent, then the
      // user retries from this still-mounted modal.
      if (err instanceof ApiError && err.code === 'kyc_consent_required') {
        setPhase('pick');
        setError('Please accept the data-processing notice, then try the upload again.');
        onConsentRequired();
        return;
      }
      setPhase('pick');
      setError(err instanceof ApiError ? err.serverMessage : (err as Error).message || 'Upload failed.');
    }
  }

  function abort() {
    abortRef.current?.abort();
  }

  const busy = phase === 'requesting' || phase === 'uploading' || phase === 'recording';

  return (
    <div className="kyc-modal-backdrop" role="dialog" aria-modal="true" aria-label={`${mode === 'replace' ? 'Replace' : 'Upload'} ${category.label}`}>
      <div className="kyc-modal card">
        <div className="kyc-modal-hd">
          <h3 style={{ fontSize: 17, margin: 0 }}>
            {mode === 'replace' ? 'Replace document' : 'Upload document'}
          </h3>
          <button
            type="button"
            className="kyc-modal-x"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ fontSize: 13.5, color: 'var(--fg-soft)', marginTop: 4 }}>{category.label}</div>
        {category.description && (
          <p style={{ fontSize: 12.5, color: 'var(--fg-mute)', marginTop: 8, lineHeight: 1.55 }}>
            {category.description}
          </p>
        )}

        {phase === 'pick' && (
          <>
            <div className="field" style={{ marginTop: 18 }}>
              <label>Choose a file (PDF, JPEG or PNG · max 10MB)</label>
              <input
                type="file"
                accept={ACCEPT_ATTR}
                onChange={(e) => onPick(e.target.files?.[0] ?? null)}
              />
            </div>
            {file && (
              <div style={{ fontSize: 12.5, color: 'var(--fg-soft)', marginTop: 8, fontFamily: 'var(--mono)' }}>
                {file.name} · {(file.size / 1024 / 1024).toFixed(2)}MB
              </div>
            )}
          </>
        )}

        {(phase === 'requesting' || phase === 'uploading' || phase === 'recording') && (
          <div style={{ marginTop: 20 }}>
            <div className="kyc-progress" aria-hidden="true">
              <div
                className="kyc-progress-bar"
                style={{ width: phase === 'uploading' ? `${progress}%` : '100%' }}
              />
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--fg-soft)', marginTop: 8 }}>
              {phase === 'requesting' && 'Preparing secure upload…'}
              {phase === 'uploading' && `Uploading… ${progress}%`}
              {phase === 'recording' && 'Recording your submission…'}
            </div>
          </div>
        )}

        {phase === 'orphan' && (
          <div style={{ marginTop: 18, padding: 14, borderRadius: 'var(--r-sm)', background: 'var(--accent-soft)', fontSize: 13, lineHeight: 1.55 }}>
            Your file uploaded successfully but we could not record it. No need to
            re-upload — just retry recording it below.
          </div>
        )}

        {error && (
          <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 14, lineHeight: 1.5 }}>{error}</div>
        )}

        <div className="kyc-modal-actions">
          {phase === 'uploading' ? (
            <button type="button" className="btn ghost" onClick={abort}>Cancel upload</button>
          ) : (
            <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>Close</button>
          )}

          {phase === 'pick' && (
            <button type="button" className="btn accent" onClick={start} disabled={!file}>
              {mode === 'replace' ? 'Replace →' : 'Upload →'}
            </button>
          )}
          {phase === 'orphan' && orphanRef.current && (
            <button
              type="button"
              className="btn accent"
              onClick={() => {
                const o = orphanRef.current!;
                recordMetadata(o.blob_name, { name: o.filename, type: file?.type ?? '', size: file?.size ?? 0 });
              }}
            >
              Retry recording →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
